import { useSuspenseQuery } from '@apollo/client';
import React, { Suspense } from 'react';
import { useParams } from 'react-router-dom';

import { TableSkeleton } from '@eluve/blocks';
import { Stack } from '@eluve/components';
import { graphql } from '@eluve/graphql.tada';

import {
  PatientConditions,
  patientConditionsFragment,
} from './PatientConditions';
import { PatientDocuments, patientDocumentsFragment } from './PatientDocuments';
import {
  PatientLabResults,
  patientDiagnosticReportsFragment,
} from './PatientLabResults';

const patientArtifactsQuery = graphql(
  `
    query patientArtifacts($tenantId: uuid!, $patientId: uuid!) {
      patientsByPk(tenantId: $tenantId, id: $patientId) {
        __typename
        id
        observations {
          __typename
          id
          name
          referenceRange
          unit
          createdAt
        }
        ...PatientConditions
        ...PatientDiagnosticReports
        ...PatientDocuments
      }
    }
  `,
  [
    patientConditionsFragment,
    patientDiagnosticReportsFragment,
    patientDocumentsFragment,
  ],
);

export interface PatientArtifactsProps {}

const PatientArtifactsContent: React.FC = () => {
  const { patientId, tenantId } = useParams() as {
    tenantId: string;
    patientId: string;
  };

  useSuspenseQuery(patientArtifactsQuery, {
    variables: {
      tenantId,
      patientId,
    },
  });

  return (
    <Stack gap={6} className="pt-2">
      <PatientDocuments patientId={patientId} tenantId={tenantId} />
      <PatientLabResults patientId={patientId} />
      <PatientConditions patientId={patientId} />
    </Stack>
  );
};

export const PatientArtifacts: React.FC<PatientArtifactsProps> = () => {
  return (
    <Suspense fallback={<TableSkeleton rowCount={5} />}>
      <PatientArtifactsContent />
    </Suspense>
  );
};
